import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  BuildingDetailsSection,
  DetailsGrid,
  DetailsSection,
  DetailsTitle,
  DetailRow,
  DetailLabel,
  DetailValue,
} from '../BuildingDetailsTab.styles'
import {
  type BuildingData,
  formatArea,
  BUILDING_VALUES,
} from '../utils/buildingData.utils'

interface BuildingDetailsProps {
  building: BuildingData
}

export const BuildingDetails: React.FC<BuildingDetailsProps> = ({
  building,
}) => {
  const { t } = useTranslation('land-analysis')
  const { t: tCommon } = useTranslation('common')
  const na = tCommon('units.na')

  // Map marker values to translated strings
  const valueMap: Record<string, string> = {
    [BUILDING_VALUES.NOT_PROTECTED]: t('buildingDetails.values.notProtected'),
    [BUILDING_VALUES.HERITAGE_PROTECTED]: t(
      'buildingDetails.values.heritageProtected'
    ),
    [BUILDING_VALUES.COMPLIANT]: t('buildingDetails.values.compliant'),
    [BUILDING_VALUES.NOT_CLASSIFIED]: t('buildingDetails.values.notClassified'),
    [BUILDING_VALUES.NOT_SPECIFIED]: t('buildingDetails.values.notSpecified'),
    [BUILDING_VALUES.NA]: t('buildingDetails.values.na') || na,
    [BUILDING_VALUES.SOLAR_EXCELLENT]: t(
      'buildingDetails.values.solarExcellent'
    ),
    [BUILDING_VALUES.SOLAR_GOOD]: t('buildingDetails.values.solarGood'),
    [BUILDING_VALUES.SOLAR_MEDIUM]: t('buildingDetails.values.solarMedium'),
    [BUILDING_VALUES.SOLAR_LOW]: t('buildingDetails.values.solarLow'),
    [BUILDING_VALUES.PROJECTED]: t('buildingDetails.values.projected'),
  }

  const tv = (value: string | undefined) => {
    if (!value) return ''
    return valueMap[value] ?? value
  }

  return (
    <BuildingDetailsSection>
      <DetailsGrid>
        <DetailsSection>
          <DetailsTitle>
            {t('buildingDetails.sections.dimensions.title')}
          </DetailsTitle>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.dimensions.groundArea')}
            </DetailLabel>
            <DetailValue
              className={building.isAreaEstimated ? 'highlight' : ''}
            >
              {tv(formatArea(building.area))}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.dimensions.volume')}
            </DetailLabel>
            <DetailValue
              className={building.isVolumeEstimated ? 'highlight' : ''}
            >
              {building.volume ? `${building.volume.toLocaleString()} m³` : na}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.dimensions.height')}
            </DetailLabel>
            <DetailValue
              className={building.isHeightEstimated ? 'highlight' : ''}
            >
              {building.height ? `${building.height.toLocaleString()} m` : na}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.dimensions.estFloorArea')}
            </DetailLabel>
            <DetailValue
              className={building.isFloorAreaEstimated ? 'highlight' : ''}
            >
              {tv(formatArea(building.floorArea))}
            </DetailValue>
          </DetailRow>
        </DetailsSection>

        <DetailsSection>
          <DetailsTitle>
            {t('buildingDetails.sections.status.title')}
          </DetailsTitle>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.status.constructionStatus')}
            </DetailLabel>
            <DetailValue>{tv(building.constructionStatus)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.status.protectionStatus')}
            </DetailLabel>
            <DetailValue>{tv(building.protectionStatus)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.status.lastRenovation')}
            </DetailLabel>
            <DetailValue>{building.lastRenovation || na}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.status.compliance')}
            </DetailLabel>
            <DetailValue>{tv(building.compliance)}</DetailValue>
          </DetailRow>
        </DetailsSection>

        <DetailsSection>
          <DetailsTitle>
            {t('buildingDetails.sections.propertyInfo.title')}
          </DetailsTitle>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.propertyInfo.officialNumber')}
            </DetailLabel>
            <DetailValue>{building.officialNumber || na}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.propertyInfo.ofsCategory')}
            </DetailLabel>
            <DetailValue>{tv(building.ofsCategory)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.propertyInfo.classification')}
            </DetailLabel>
            <DetailValue>{tv(building.classification)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.propertyInfo.units')}
            </DetailLabel>
            <DetailValue>{building?.units?.toString()}</DetailValue>
          </DetailRow>
        </DetailsSection>

        <DetailsSection>
          <DetailsTitle>
            {t('buildingDetails.sections.energy.title')}
          </DetailsTitle>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.energy.mainHeating')}
            </DetailLabel>
            <DetailValue>{tv(building.mainHeating)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.energy.secondaryHeating')}
            </DetailLabel>
            <DetailValue>{tv(building.secondaryHeating)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.energy.waterHeating')}
            </DetailLabel>
            <DetailValue>{tv(building.waterHeating)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>
              {t('buildingDetails.sections.energy.solarPotential')}
            </DetailLabel>
            <DetailValue>{tv(building.solarPotential)}</DetailValue>
          </DetailRow>
        </DetailsSection>
      </DetailsGrid>
    </BuildingDetailsSection>
  )
}
